import { useEffect, useState } from 'react';
import { RiInformationLine, RiLoader4Line } from '@remixicon/react';
import Modal from '@components/ui/Modal';
import { storage } from '@/lib/storage';

/**
 * Has the given broker dismissed the Payment Settlement Notice for the
 * given scope? Dismissal is per-broker — once a broker ticks "Don't show
 * again" for quote captures, they won't see the quote-scope notice again
 * on any future quote. `storageKey` selects the scope (quote vs invoice —
 * see `storageKeys` in `@/lib/storage`); `brokerId` is the current user's
 * userprofile_id.
 *
 * Missing `brokerId` → returns false, so the modal will show. That's the
 * safe default: never silently skip a notice we can't tie to a broker.
 */
export const isSettlementNoticeDismissed = (storageKey, brokerId) => {
  if (!storageKey || !brokerId) return false;
  const dismissed = storage.getItem(storageKey);
  return Array.isArray(dismissed) && dismissed.includes(brokerId);
};

/**
 * PaymentSettlementNoticeModal
 * One-time informational gate shown before capturing card funds. Explains
 * the 1–3 day settlement window and offers a "Don't show again" opt-out
 * that persists to localStorage.
 *
 * The component owns the checkbox state and the localStorage write; the
 * parent controls open/close and runs the actual capture in `onConfirm`.
 *
 * @param {Object}   props
 * @param {boolean}  props.isOpen     — controls modal visibility
 * @param {Function} props.onClose    — fired on Cancel or outside click
 * @param {Function} props.onConfirm  — fired on Confirm (after persisting the
 *                                     "don't show again" preference)
 * @param {string}   props.storageKey — localStorage key that stores the
 *                                     scope's list of dismissed broker IDs.
 *                                     Use one of the `*_SETTLEMENT_NOTICE_DISMISSED`
 *                                     values from `storageKeys` so quote
 *                                     and invoice captures stay independent.
 * @param {string}   props.brokerId   — current user's userprofile_id. When
 *                                     "Don't show again" is checked at
 *                                     confirm time, this id is appended
 *                                     to the scope's dismissed list.
 * @param {boolean}  [props.isLoading]  — disables buttons and shows the
 *                                       Confirm-button spinner while the
 *                                       parent's capture flow is in flight
 */
const PaymentSettlementNoticeModal = ({ isOpen, onClose, onConfirm, storageKey, brokerId, isLoading = false }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Reset the checkbox each time the modal reopens so a prior "checked"
  // state doesn't sneak into an unrelated capture.
  useEffect(() => {
    if (isOpen) setDontShowAgain(false);
  }, [isOpen]);

  const handleConfirm = () => {
    if (dontShowAgain && storageKey && brokerId) {
      const current = storage.getItem(storageKey);
      const list = Array.isArray(current) ? current : [];
      if (!list.includes(brokerId)) {
        storage.setItem(storageKey, [...list, brokerId]);
      }
    }
    onConfirm?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => (isLoading ? null : onClose?.())}
      size="sm"
    >
      <div className="p-6 space-y-4">
        <h2 className="text-xl font-semibold text-primary-500">Payment Settlement Notice</h2>
        <p className="text-sm text-gray-700 leading-relaxed">
          Please note that the settlement of captured funds by card typically
          takes <span className="font-semibold">1–3 days</span>. Funds are subject to final bank
          clearing and carry a slight risk of network decline.
        </p>
        <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span>Don't show this again.</span>
        </label>
        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed border"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading}
            className="flex-1 px-4 py-2 text-sm text-white bg-primary-700 rounded-full hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {isLoading && <RiLoader4Line className="w-4 h-4 animate-spin" />}
            Confirm
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PaymentSettlementNoticeModal;
